import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, MapPin, Phone, ArrowRight, Loader2, Send, Sparkles, ChevronDown, CheckCircle2 } from 'lucide-react';
import { useNewsletter } from '../../hooks/useNewsletter';
import { useReducedMotion } from '../../hooks/useReducedMotion';

const FooterLink = ({ to, children }) => (
  <Link to={to} className="group inline-flex items-center gap-2 text-primary-100/80 hover:text-white text-sm py-1.5 transition-colors w-full">
    <motion.span 
      initial={{ x: -5, opacity: 0 }} 
      whileHover={{ x: 0, opacity: 1 }} 
      transition={{ duration: 0.2 }}
      className="text-primary-400 flex-shrink-0"
    >
      <ArrowRight className="h-3.5 w-3.5" />
    </motion.span>
    <motion.span whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
      {children}
    </motion.span>
  </Link>
);

const FooterSection = ({ title, children, isOpen, onToggle, isMobile }) => {
  return (
    <div className="border-b border-white/10 md:border-none py-4 md:py-0">
      <button 
        onClick={onToggle}
        disabled={!isMobile}
        className="flex justify-between items-center w-full md:cursor-default"
      >
        <h3 className="text-sm font-semibold uppercase tracking-wider text-white">
          {title}
        </h3>
        {isMobile && (
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
            <ChevronDown className="h-4 w-4 text-primary-300" />
          </motion.div>
        )}
      </button>
      <AnimatePresence initial={false}>
        {(isOpen || !isMobile) && (
          <motion.div
            key="content"
            initial={isMobile ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={isMobile ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden md:overflow-visible"
          >
            <div className="pt-4 md:pt-6 space-y-1">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { subscribe } = useNewsletter();
  const reduceMotion = useReducedMotion();
  
  // Mobile accordion state
  const [openSection, setOpenSection] = useState('links'); // default open first section
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle resize for mobile accordion logic
  useState(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      setIsSubscribing(true);
      await subscribe(email);
      setIsSuccess(true);
      setEmail('');
      setTimeout(() => setIsSuccess(false), 3000); // Reset success state after 3s
    } catch {
      // Error handled by useNewsletter hook
    } finally {
      setIsSubscribing(false);
    }
  };

  const toggleSection = (section) => {
    if (isMobile) {
      setOpenSection(openSection === section ? '' : section);
    }
  };

  return (
    <footer className="relative bg-gradient-to-b from-primary-900 to-[#021f12] text-white border-t border-primary-800/40 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-12">
          
          {/* Column 1: Brand & Mission */}
          <div className="space-y-6 pb-6 md:pb-0 border-b border-white/10 md:border-none">
            <Link to="/" className="flex items-center space-x-3 group w-fit">
              <motion.img
                whileHover={{ rotate: 10, scale: 1.05 }}
                src="/assets/logo/logo-new.png"
                alt={t('common.logo_alt')}
                className="h-12 w-12 rounded-2xl object-cover ring-1 ring-white/20 shadow-lg"
              />
              <span className="text-xl font-bold tracking-tight text-white group-hover:text-primary-200 transition-colors">
                PROTEGE QV
              </span>
            </Link>
            <p className="text-primary-100/80 text-sm leading-relaxed pr-4">
              {t('footer.about')}
            </p>
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-primary-200 text-xs font-medium backdrop-blur-sm"
            >
              <Sparkles className="h-4 w-4 text-emerald-400 flex-shrink-0" />
              <span>{t('footer.values')}</span>
            </motion.div>
          </div>

          {/* Column 2: Navigation Links (Accordion on Mobile) */}
          <FooterSection 
            title={t('footer.links')} 
            isOpen={openSection === 'links'} 
            onToggle={() => toggleSection('links')}
            isMobile={isMobile}
          >
            <FooterLink to="/books">{t('nav.books')}</FooterLink>
            <FooterLink to="/groups">{t('nav.groups')}</FooterLink>
            <FooterLink to="/events">{t('nav.events')}</FooterLink>
            <FooterLink to="/about">{t('nav.about')}</FooterLink>
          </FooterSection>

          {/* Column 3: Community & Engagement */}
          <FooterSection 
            title={t('community.title')} 
            isOpen={openSection === 'community'} 
            onToggle={() => toggleSection('community')}
            isMobile={isMobile}
          >
            <FooterLink to="/contact">{t('nav.contact')}</FooterLink>
            <FooterLink to="/community">{t('community.donate_title')}</FooterLink>
            <FooterLink to="/community">{t('community.volunteer_title')}</FooterLink>
          </FooterSection>

          {/* Column 4: Newsletter & Direct Contact */}
          <div className="pt-6 md:pt-0">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white mb-6">
              {t('footer.newsletter.title')}
            </h3>
            
            <form onSubmit={handleNewsletterSubmit} className="space-y-3 relative">
              <div className="relative group">
                <input
                  type="email"
                  placeholder={t('footer.newsletter.placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-4 pr-12 py-3 rounded-xl text-sm bg-black/20 border border-white/10 text-white placeholder-primary-200/50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all backdrop-blur-sm"
                  required
                  disabled={isSubscribing || isSuccess}
                />
                <motion.button
                  type="submit"
                  disabled={isSubscribing || isSuccess || !email.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="absolute right-1.5 top-1.5 bottom-1.5 px-3 bg-emerald-500 text-white rounded-lg flex items-center justify-center disabled:opacity-50 disabled:bg-white/10 transition-colors"
                >
                  <AnimatePresence mode="wait">
                    {isSuccess ? (
                      <motion.div key="success" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                        <CheckCircle2 className="h-4 w-4" />
                      </motion.div>
                    ) : isSubscribing ? (
                      <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </motion.div>
                    ) : (
                      <motion.div key="send" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <Send className="h-4 w-4" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
              <p className="text-primary-200/60 text-xs">
                {isSuccess ? <span className="text-emerald-400">Inscription réussie ! Merci.</span> : t('footer.newsletter.desc')}
              </p>
            </form>

            <div className="mt-8 space-y-4 text-sm text-primary-100/80">
              <motion.a 
                href="https://maps.google.com" 
                target="_blank" 
                rel="noreferrer"
                whileHover={{ x: 4, color: '#fff' }}
                className="flex items-start gap-3 group"
              >
                <div className="p-2 rounded-lg bg-white/5 group-hover:bg-emerald-500/20 transition-colors">
                  <MapPin className="h-4 w-4 text-emerald-400" />
                </div>
                <span className="mt-1.5">{t('footer.address', 'Rond-Point Express, Yaoundé, Cameroun')}</span>
              </motion.a>
              
              <motion.a 
                href="mailto:contact@protegeqv.org"
                whileHover={{ x: 4, color: '#fff' }}
                className="flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-white/5 group-hover:bg-emerald-500/20 transition-colors">
                  <Mail className="h-4 w-4 text-emerald-400" />
                </div>
                <span>contact@protegeqv.org</span>
              </motion.a>
              
              <motion.a 
                href="https://wa.me/237699936028"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 4, color: '#fff' }}
                className="flex items-center gap-3 group"
              >
                <div className="p-2 rounded-lg bg-white/5 group-hover:bg-emerald-500/20 transition-colors">
                  <Phone className="h-4 w-4 text-emerald-400" />
                </div>
                <span>+237 699 936 028 (WhatsApp)</span>
              </motion.a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-primary-200/60 font-medium">
          <p className="text-center md:text-left">
            {t('footer.copyright', { year: currentYear })}
          </p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

